import { useState, useEffect, useCallback } from 'react';

interface QueuedUpdate {
  id: string;
  roundId: string;
  timestamp: number;
  updates: {
    holes?: any[];
    currentHole?: number;
    confirmedHoles?: number[];
    status?: string;
  };
}

const QUEUE_KEY = 'settleup_offline_queue';

export const useOfflineQueue = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingUpdates, setPendingUpdates] = useState<QueuedUpdate[]>([]);

  // Load queued updates from localStorage on mount - with automatic stale cleanup
  useEffect(() => {
    const stored = localStorage.getItem(QUEUE_KEY);
    if (stored) {
      try {
        const parsed: QueuedUpdate[] = JSON.parse(stored);
        const now = Date.now();
        const FOUR_HOURS = 4 * 60 * 60 * 1000; // Extended from 10 min to 4 hours for golf rounds
        
        // Filter out updates older than 4 hours (realistic for a full golf round)
        const freshUpdates = parsed.filter(u => now - u.timestamp < FOUR_HOURS);
        
        if (freshUpdates.length !== parsed.length) {
          console.log(`🧹 Cleared ${parsed.length - freshUpdates.length} stale queued updates (>4 hours old)`);
        }
        
        if (freshUpdates.length > 0) {
          setPendingUpdates(freshUpdates);
          localStorage.setItem(QUEUE_KEY, JSON.stringify(freshUpdates));
        } else {
          localStorage.removeItem(QUEUE_KEY);
        }
      } catch (e) {
        console.error('Failed to parse offline queue:', e);
        localStorage.removeItem(QUEUE_KEY);
      }
    }
  }, []);

  // Persist queue to localStorage whenever it changes
  useEffect(() => {
    if (pendingUpdates.length > 0) {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(pendingUpdates));
    } else {
      localStorage.removeItem(QUEUE_KEY);
    }
  }, [pendingUpdates]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Back online - ready to sync');
      setIsOnline(true);
    };

    const handleOffline = () => {
      console.log('📴 Gone offline - queuing updates');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Add an update to the queue
  const queueUpdate = useCallback((roundId: string, updates: QueuedUpdate['updates']) => {
    const newUpdate: QueuedUpdate = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      roundId,
      timestamp: Date.now(),
      updates,
    };

    setPendingUpdates(prev => {
      // Merge with existing update for same round if exists (keep latest)
      const existingIndex = prev.findIndex(u => u.roundId === roundId);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          timestamp: Date.now(),
          updates: {
            ...updated[existingIndex].updates,
            ...updates,
          },
        };
        return updated;
      }
      return [...prev, newUpdate];
    });

    return newUpdate;
  }, []);

  // Get updates for a specific round
  const getQueuedUpdatesForRound = useCallback((roundId: string) => {
    return pendingUpdates.filter(u => u.roundId === roundId);
  }, [pendingUpdates]);

  // Clear processed updates
  const clearProcessedUpdates = useCallback((processedIds: string[]) => {
    setPendingUpdates(prev => prev.filter(u => !processedIds.includes(u.id)));
  }, []);

  // Clear all updates for a round
  const clearRoundUpdates = useCallback((roundId: string) => {
    setPendingUpdates(prev => prev.filter(u => u.roundId !== roundId));
  }, []);

  // Clear all pending updates (for dismissing stale data)
  const clearAllUpdates = useCallback(() => {
    setPendingUpdates([]);
    localStorage.removeItem(QUEUE_KEY);
  }, []);

  return {
    isOnline,
    pendingUpdates,
    queueUpdate,
    getQueuedUpdatesForRound,
    clearProcessedUpdates,
    clearRoundUpdates,
    clearAllUpdates,
    hasPendingUpdates: pendingUpdates.length > 0,
  };
};
